import React, { useEffect, useRef } from "react";
import { GameResultStatus } from "../types";

// -----------------------------------------------------------------------------
// Hook: useBeatLoop
// - encapsulates the beat-starting loop, animation handling and keyboard handling
// - does NOT change any external state directly except through provided
//   callback handlers (onNoReaction, onValidMove, onInvalidMove)
// -----------------------------------------------------------------------------
export function useBeatLoop({
  monitorRef,
  centerRef,
  leftLineRef,
  rightLineRef,
  LINE_WIDTH_PX,
  TARGET_SUCCESS_SCORE,
  LINE_ANIM_DURATION_MS,
  isAnimatingRef,
  gameResultRef,
  onNoReaction,
  onValidMove,
  onInvalidMove,
  playEffect,
  invalidMoveSoundLocal,
}: {
  monitorRef: React.RefObject<HTMLDivElement | null>;
  centerRef: React.RefObject<HTMLDivElement | null>;
  leftLineRef: React.RefObject<HTMLDivElement | null>;
  rightLineRef: React.RefObject<HTMLDivElement | null>;
  LINE_WIDTH_PX: number;
  TARGET_SUCCESS_SCORE: number;
  LINE_ANIM_DURATION_MS: number;
  isAnimatingRef: React.MutableRefObject<boolean>;
  gameResultRef: React.MutableRefObject<GameResultStatus>;
  onNoReaction: () => void;
  onValidMove: () => void;
  onInvalidMove: () => void;
  playEffect: (src: string) => void;
  invalidMoveSoundLocal: string;
}) {
  const leftAnimRef = useRef<Animation | null>(null);
  const rightAnimRef = useRef<Animation | null>(null);
  const nextBeatTimeoutRef = useRef<number | null>(null);
  const spacePressedRef = useRef<boolean>(false);
  const isNoReactionFailRef = useRef<boolean>(false);

  function safeClearTimeout(ref: React.MutableRefObject<number | null>) {
    if (ref.current != null) {
      window.clearTimeout(ref.current);
      ref.current = null;
    }
  }

  function cancelAnimsAndResetStyles() {
    if (leftAnimRef.current) {
      leftAnimRef.current.cancel();
      leftAnimRef.current = null;
    }
    if (rightAnimRef.current) {
      rightAnimRef.current.cancel();
      rightAnimRef.current = null;
    }
    if (leftLineRef.current) {
      leftLineRef.current.style.transform = "";
      leftLineRef.current.style.opacity = "";
    }
    if (rightLineRef.current) {
      rightLineRef.current.style.transform = "";
      rightLineRef.current.style.opacity = "";
    }
  }

  function rect(el: Element | null) {
    return el ? el.getBoundingClientRect() : null;
  }

  function isOverlappingCenter(): boolean {
    const centerRect = rect(centerRef.current);
    if (!centerRect) return false;
    const leftRect = rect(leftLineRef.current);
    const rightRect = rect(rightLineRef.current);
    const overlaps = (r1: DOMRect | null, r2: DOMRect) =>
      !r1
        ? false
        : !(
            r1.right <= r2.left ||
            r1.left >= r2.right ||
            r1.bottom <= r2.top ||
            r1.top >= r2.bottom
          );
    return overlaps(leftRect, centerRect) || overlaps(rightRect, centerRect);
  }

  function computePositions() {
    const container = monitorRef.current;
    if (!container) return { leftTranslate: 0 };
    const rectC = container.getBoundingClientRect();
    const containerWidth = rectC.width;
    const lineHalf = LINE_WIDTH_PX / 2;
    const leftTargetLeft = containerWidth / 2 - lineHalf;
    const leftStartInset = 8;
    const leftTranslate = leftTargetLeft - leftStartInset;
    return { leftTranslate: Math.round(leftTranslate) };
  }

  function startOneBeat() {
    if (!isAnimatingRef.current || gameResultRef.current) return;

    cancelAnimsAndResetStyles();
    safeClearTimeout(nextBeatTimeoutRef);
    spacePressedRef.current = false;

    const { leftTranslate } = computePositions();
    const leftEl = leftLineRef.current!;
    const rightEl = rightLineRef.current!;

    leftAnimRef.current = leftEl.animate(
      [
        { transform: "translateX(0px)" },
        { transform: `translateX(${leftTranslate}px)` },
      ],
      { duration: LINE_ANIM_DURATION_MS, easing: "linear", fill: "forwards" }
    );
    rightAnimRef.current = rightEl.animate(
      [
        { transform: "translateX(0px)" },
        { transform: `translateX(${-leftTranslate}px)` },
      ],
      { duration: LINE_ANIM_DURATION_MS, easing: "linear", fill: "forwards" }
    );

    nextBeatTimeoutRef.current = window.setTimeout(() => {
      if (!spacePressedRef.current) {
        // fire the no-reaction handler (plays sound + applies penalty)
        playEffect(invalidMoveSoundLocal);
        isNoReactionFailRef.current = true;
        setTimeout(() => {
          isNoReactionFailRef.current = false;
        }, 200);
        onNoReaction();
      }

      cancelAnimsAndResetStyles();
      if (!gameResultRef.current && isAnimatingRef.current) startOneBeat();
    }, LINE_ANIM_DURATION_MS);
  }

  // start the loop and resize listener
  useEffect(() => {
    startOneBeat();
    const onResize = () => computePositions();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // keyboard handling
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.code !== "Space") return;
      if (
        !isAnimatingRef.current ||
        gameResultRef.current ||
        isNoReactionFailRef.current
      )
        return;

      const valid = isOverlappingCenter();
      spacePressedRef.current = true;
      cancelAnimsAndResetStyles();
      safeClearTimeout(nextBeatTimeoutRef);

      if (valid) {
        onValidMove();
      } else {
        onInvalidMove();
      }

      requestAnimationFrame(() => {
        if (!gameResultRef.current && isAnimatingRef.current) startOneBeat();
      });
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function resetLoop() {
    safeClearTimeout(nextBeatTimeoutRef);
    cancelAnimsAndResetStyles();
    requestAnimationFrame(() => startOneBeat());
  }

  // expose only what the caller needs
  return {
    startOneBeat,
    resetLoop,
    // exposing refs in case caller wants to inspect (kept minimal)
    _internal: { leftAnimRef, rightAnimRef, nextBeatTimeoutRef },
  };
}
